// @ts-strict-ignore
import { type ReactElement } from "react";

import { Arrange, Typography } from "@aviary";
import { CatalogSortDropdown } from "@shared/components/CatalogSortDropdown/CatalogSortDropdown";
import { useBreakpoints } from "@shared/hooks/useBreakpoints/useBreakpoints";
import { useCatalogHideBrandsCheck } from "@shared/hooks/useCatalogHideBrandsCheck/useCatalogHideBrandsCheck";
import { useCatalogSearch } from "@shared/hooks/useCatalogSearch/useCatalogSearch";
import { useOptimizedMegaMenuTaxonomyTree } from "@shared/hooks/useOptimizedMegaMenuTaxonomyTree/useOptimizedMegaMenuTaxonomyTree";
import { useOptimizedTaxonomyTags } from "@shared/hooks/useOptimizedTaxonomyTags/useOptimizedTaxonomyTags";
import {
  catalogSearchResultTitle,
  useCatalogSearchResultTitle,
} from "@shared/reactiveVar/useCatalogSearchResultTitle.store";
import { isFullscriptJsVar } from "@shared/reactiveVar/useIsFullscriptJs/useIsFullscriptJs.store";
import { isInOfficeCheckoutVar } from "@shared/reactiveVar/useIsInOfficeCheckoutVar/useIsInOfficeCheckoutVar.store";
import type { Taxonomy } from "@shared/types/graphqlGenerated";

import { FilterButton, FilterButtonStyle } from "../FilterButton/FilterButton";
import { SearchFilterTags } from "../SearchFilterTags/SearchFilterTags";

import * as styles from "./MegaMenuFilterTags.styles";

interface Props {
  onClearFilters: () => void;
  resultCount: number;
  resultCountDescription?: string | (ReactElement | string)[];
  isFS2Wholesale?: boolean;
  hideTitle?: boolean;
}

const MegaMenuFilterTags = ({
  onClearFilters,
  resultCount,
  resultCountDescription,
  isFS2Wholesale,
  hideTitle = false,
}: Props) => {
  const { tablet, desktop } = useBreakpoints();
  const { optimizedTaxonomySearchTags } = useOptimizedMegaMenuTaxonomyTree();
  const searchResultTitle = useCatalogSearchResultTitle();
  const shouldHideBrands = useCatalogHideBrandsCheck();
  const isInOfficeCheckout = isInOfficeCheckoutVar();
  const isFullscriptJs = isFullscriptJsVar();

  const {
    activeSearchType,
    activeIngredients,
    excludedIngredients,
    activeBrands,
    activeCategories,
    activeConditions,
    activeTaxonomies,
    activeOptimizedTaxonomies,
    activeSupplementTypes,
    activeFilters,
    isOnlyShowFavorites,
    isOnlyShowInStock,
    searchQuery,
    isFsTested,
    setActiveOptimizedTaxonomies,
  } = useCatalogSearch();

  const optimizedTaxonomyTags = useOptimizedTaxonomyTags({
    activeOptimizedTaxonomies,
    optimizedTaxonomySearchTags,
  });

  const getActiveFilterCount = (): number => {
    let totalActiveFilters = 0;

    if (isOnlyShowFavorites) {
      totalActiveFilters += 1;
    }

    if (isOnlyShowInStock) {
      totalActiveFilters += 1;
    }

    if (isFsTested) {
      totalActiveFilters += 1;
    }

    if (!shouldHideBrands) {
      totalActiveFilters += activeBrands?.length ?? 0;
    }

    totalActiveFilters +=
      (activeFilters?.length ?? 0) +
      (activeIngredients?.length ?? 0) +
      (activeCategories?.length ?? 0) +
      (excludedIngredients?.length ?? 0) +
      (activeConditions?.length ?? 0) +
      (activeTaxonomies?.length ?? 0) +
      (optimizedTaxonomyTags?.length ?? 0) +
      (activeSupplementTypes?.length ?? 0);

    return totalActiveFilters;
  };

  const hasFilter = !!(
    (!shouldHideBrands && activeBrands?.length > 0) ||
    activeIngredients?.length > 0 ||
    activeCategories?.length > 0 ||
    excludedIngredients?.length > 0 ||
    activeFilters?.length > 0 ||
    activeSupplementTypes?.length > 0 ||
    activeConditions?.length > 0 ||
    activeTaxonomies?.length > 0 ||
    activeOptimizedTaxonomies?.length > 0 ||
    isOnlyShowInStock ||
    isOnlyShowFavorites ||
    isFsTested
  );

  const getSearchPageTitle = () => {
    if (searchQuery) {
      return `"${searchQuery}"`;
    }

    if (searchResultTitle.type === "taxonomy") {
      return (searchResultTitle.title as Taxonomy).name as string;
    }

    if (shouldHideBrands && activeBrands.length === 1) {
      return activeBrands[0].name as string;
    }

    if (
      getActiveFilterCount() === 1 &&
      searchResultTitle.type === "ingredient" &&
      activeIngredients.length === 1 &&
      activeIngredients[0].name === searchResultTitle.title
    ) {
      return searchResultTitle.title as string;
    }

    return "";
  };

  const shouldRenderSearchResult = hasFilter || shouldHideBrands || !!searchQuery;
  const isDisabledSort = shouldRenderSearchResult && (!resultCount || resultCount <= 1);

  if (!shouldRenderSearchResult || (!hasFilter && desktop.lessThan)) {
    return null;
  }

  const hasMoreThanOneActiveFilter = getActiveFilterCount() > 1;

  const getMaxFilterTagsToShow = () => {
    if (activeSearchType === "filters") {
      return 2;
    }

    return 3;
  };

  if (isInOfficeCheckout || isFullscriptJs) {
    return (
      <Arrange css={styles.filterAndSearch} alignment="center">
        <div>
          {!!hasFilter && (
            <SearchFilterTags onClearFilters={!!hasMoreThanOneActiveFilter && onClearFilters} />
          )}
        </div>
        {!!shouldRenderSearchResult && !!tablet.greaterThan && (
          <div>
            <CatalogSortDropdown isDisabled={isDisabledSort} />
          </div>
        )}
      </Arrange>
    );
  }

  const onClearAllFilters = () => {
    const searchTitle = catalogSearchResultTitle();

    if (
      activeOptimizedTaxonomies?.length > 0 &&
      searchTitle.type === "taxonomy" &&
      !isFS2Wholesale &&
      !isInOfficeCheckout &&
      !isFullscriptJs
    ) {
      const searchOptimizedTaxonomies = activeOptimizedTaxonomies.filter(
        tax => tax.type === "search"
      );
      setActiveOptimizedTaxonomies(
        [
          {
            name: (searchTitle.title as Taxonomy).name,
            id: (searchTitle.title as Taxonomy).id,
            hideFromDisplay: true,
            type: "lock",
          },
          ...searchOptimizedTaxonomies,
        ],
        true
      );

      return;
    }

    onClearFilters();
  };

  const renderSearchPageTitle = () => {
    if (hideTitle) return;

    return (
      <Typography type="h3" isMarginless>
        {getSearchPageTitle()}
      </Typography>
    );
  };

  return (
    <div css={styles.container}>
      <div css={styles.topRow}>
        <div css={styles.titleSection}>
          {activeSearchType !== "filters" && (
            <span css={styles.filterButton}>
              <FilterButton type={FilterButtonStyle.TEXT_AND_ICON} />
            </span>
          )}
          {renderSearchPageTitle()}
        </div>
        <span css={styles.countDescription}>{resultCountDescription}</span>
      </div>

      {!!(getActiveFilterCount() > 0 || tablet.greaterThan) && (
        <div
          css={[
            styles.tagSection,
            getActiveFilterCount() === 0 ? styles.sortJustifyEnd : undefined,
          ]}
        >
          {!!hasFilter && (
            <SearchFilterTags
              optimizedTaxonomyTags={optimizedTaxonomyTags}
              onClearFilters={!!hasMoreThanOneActiveFilter && onClearAllFilters}
              maxFilterTagsToShow={getMaxFilterTagsToShow()}
              shouldHideBrands={shouldHideBrands}
            />
          )}
          {!!shouldRenderSearchResult && !!tablet.greaterThan && (
            <CatalogSortDropdown isDisabled={isDisabledSort} />
          )}
        </div>
      )}
    </div>
  );
};

export { MegaMenuFilterTags };
